import React from 'react'
import { Box, Grid2 as Grid, Paper, Typography } from '@mui/material'
import { AsnDetailFullDto } from '../../../../interfaces/Asn/AsnDetailFullDto'
import { YesNoBadge } from '../../../../components/YesNoBadge'
import { formatNonUTCDate, utcToLocal } from '../../../../utils/dateHelper'
import { SectionTitle, AsnStateChip } from './SmallComponents'

const sectionPaperSx = { height: '100%', width: '100%', overflow: 'hidden' }
const sectionBodySx = { p: 2 }

const InfoField: React.FC<{
  label: string
  value: React.ReactNode
  size?: number
}> = ({ label, value, size = 6 }) => (
  <Grid size={{ xs: size }}>
    <Typography variant='caption' color='text.secondary' component='p'>
      {label}
    </Typography>
    <Typography variant='body2' component='p'>
      {value ?? '—'}
    </Typography>
  </Grid>
)

interface AsnBlogEntry {
  asnState: string
  createdDate?: string
  createdUser?: string
}

export const AsnDetailGrid: React.FC<{
  asnData?: AsnDetailFullDto
  asnBlogsDistinct: AsnBlogEntry[]
}> = ({ asnData, asnBlogsDistinct }) => (
  <Grid container spacing={2}>
    <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'flex' }}>
      <Paper variant='outlined' sx={sectionPaperSx}>
        <SectionTitle>Información general</SectionTitle>
        <Grid container spacing={1.5} sx={sectionBodySx}>
          <InfoField
            label='Tipo de ingreso'
            value={asnData?.typeAsnIncomeName}
          />
          <InfoField
            label='Tracking Number'
            value={asnData?.trackingNumber || '—'}
          />
          <InfoField
            label='Estado'
            value={<AsnStateChip value={asnData?.asnStateName} />}
          />
          <InfoField
            label='Con FEFO'
            value={asnData && <YesNoBadge value={asnData.hasFefo} />}
          />
          <InfoField
            label='Fecha creación'
            value={utcToLocal(asnData?.createdDate, 'dd-MM-yyyy')}
          />
          <InfoField label='Almacén' value={asnData?.warehouseName} />
          <InfoField
            label='Fecha estimada recepción'
            value={formatNonUTCDate(asnData?.estimatedDate)}
          />
          <InfoField
            label='Fecha de recepción'
            value={utcToLocal(asnData?.receivedDate, 'dd-MM-yyyy')}
          />
          <InfoField label='Proveedor' value={asnData?.supplierName} />
          <InfoField label='Origen' value={asnData?.supplierOriginName} />
          <InfoField
            label='Tipo de ASN'
            value={asnData?.typeAsnSourceName}
          />
          <InfoField
            label='Tipo de documento'
            value={asnData?.typeAsnDocumentName}
          />
        </Grid>
      </Paper>
    </Grid>

    <Grid size={{ xs: 12, md: 6 }} sx={{ display: 'flex' }}>
      <Grid
        container
        spacing={2}
        sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}
      >
        <Grid size={{ xs: 12 }}>
          <Paper variant='outlined' sx={sectionPaperSx}>
            <SectionTitle>Detalle</SectionTitle>
            <Grid container spacing={1.5} sx={sectionBodySx}>
              <InfoField label='SKUs' value={asnData?.totalSkus} size={4} />
              <InfoField label='Unidades' value={asnData?.realQty} size={4} />
              <InfoField
                label='SKU Priorizado'
                value={
                  asnData && <YesNoBadge value={asnData.hasPrioritizedSku} />
                }
                size={4}
              />
              <Grid size={{ xs: 12 }}>
                <Typography variant='caption' color='text.secondary' component='p'>
                  LPN
                </Typography>
                <Typography variant='body2' component='p'>
                  {asnData?.lpns || '—'}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12 }} sx={{ flex: 1, display: 'flex' }}>
          <Paper variant='outlined' sx={sectionPaperSx}>
            <SectionTitle>Estados</SectionTitle>
            <Box
              sx={{
                ...sectionBodySx,
                display: 'flex',
                flexDirection: 'column',
                gap: 1
              }}
            >
              <Grid container spacing={1}>
                <Grid size={{ xs: 4 }}>
                  <Typography variant='caption' color='text.secondary'>
                    Estado
                  </Typography>
                </Grid>
                <Grid size={{ xs: 3 }}>
                  <Typography variant='caption' color='text.secondary'>
                    Fecha
                  </Typography>
                </Grid>
                <Grid size={{ xs: 5 }}>
                  <Typography variant='caption' color='text.secondary'>
                    Usuario
                  </Typography>
                </Grid>
              </Grid>
              {asnBlogsDistinct.map((entry) => (
                <Grid container spacing={1} key={entry.asnState}>
                  <Grid size={{ xs: 4 }}>
                    <AsnStateChip value={entry.asnState} />
                  </Grid>
                  <Grid size={{ xs: 3 }}>
                    <Typography variant='body2'>
                      {utcToLocal(entry.createdDate, 'dd.MM.yyyy')}
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 5 }}>
                    <Typography variant='body2' noWrap>
                      {entry.createdUser}
                    </Typography>
                  </Grid>
                </Grid>
              ))}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Grid>
  </Grid>
)
